import { createApp, h } from 'vue'
import { createInertiaApp, usePage, router } from '@inertiajs/vue3'

import { ZiggyVue } from 'ziggy-js/dist/vue.js';

function mergeObjects(obj1, obj2) {
    for (let key in obj1) {
      if (obj2[key] && typeof obj2[key] === 'object' && !Array.isArray(obj2[key])) {
        obj1[key] = mergeObjects(obj1[key], obj2[key]);
      } else if (obj2[key] !== null && obj2[key] !== undefined) {
        obj1[key] = obj2[key];
      }
    }
    return obj1;
  }



/**
 * setTenantColors
 * This function reads the --color-primary-100, --color-primary-200 ... from the vue page props.
 * These props are sent from the backend with the help of HandleInertiaRequests middleware
 */
const setTenantColors = () => {
    let colors = usePage().props.theme.colors;


    if(route().current().startsWith('plos.') || route().current().startsWith('plo.')){

        let pdSpecificColors = usePage().props.theme.professional_learning_opportunities.colors;

        window.pdcolors = pdSpecificColors;

        colors = mergeObjects(colors, pdSpecificColors)

        window.colors = colors;
    }

    if(route().current().startsWith('teacherGuides.')){

      let teacherGuidesSpecificColors = usePage().props.theme.teacher_guides.colors;

      colors = mergeObjects(colors, teacherGuidesSpecificColors)
    }

    if(route().current().startsWith('climateChangeExplainers.')){

      let climateChangeExplainersSpecificColors = usePage().props.theme.climate_explainers.colors;

      colors = mergeObjects(colors, climateChangeExplainersSpecificColors)
    }

    if(route().current().startsWith('resources.')){

      let externalResourcesSpecificColors = usePage().props.theme.external_resources.colors;

      colors = mergeObjects(colors, externalResourcesSpecificColors)
    }

    if(route().current().startsWith('stc.') || route().current() == 'search' ){

      let internalResourcesSpecificColors = usePage().props.theme.internal_resources.colors;

      colors = mergeObjects(colors, internalResourcesSpecificColors)
    }

    let primaryColors = {...colors.primary};
    let ctaColors = {...colors.cta};
    colors.cta = mergeObjects(primaryColors, ctaColors);

    let contrastColors = {...colors.contrast};
    let ctaContrastColors = {...colors['cta-contrast']};
    colors['cta-contrast'] = mergeObjects(contrastColors, ctaContrastColors);

    for (let colorName in colors) {
        const color = colors[colorName];
        for (let shadeKey in color) {
            const colorValue = color[shadeKey];
            document.body.style.setProperty(`--color-${colorName}-${shadeKey}`, colorValue);
        }
    }
}

import PrimaryButton from './components/PrimaryButton';

import {initAndGetAlgoliaEventDispatcher} from './Helpers/Utils.js'
import {initDocsBotAnalytics} from './Helpers/DocsBotAnalytics.js'
import {rememberChunkReload, forgetChunkReload} from './Helpers/chunkReload.js'

// NOTE FOR PRODUCTION DEPLOY:
// When merging to main, also update k8s-deploy.yml line ~135 to switch DO_SPACES_URL
// from the origin to the CDN endpoint:
//   FROM: https://subjecttoclimate.nyc3.digitaloceanspaces.com
//   TO:   https://subjecttoclimate.nyc3.cdn.digitaloceanspaces.com
// This routes all uploaded media (images, avatars, partner logos, etc.) through
// DigitalOcean's global edge CDN. The CDN endpoint has been active since 2022-07-07
// and is a zero-risk, one-line change. Do the same for LASSO_DO_SPACES_URL.

createInertiaApp({
    resolve: name => {
        if (!name) return null;
        return import(/* webpackChunkName: "pages/[request]" */ `./Pages/${name}.vue`)
            .then(page => {
                // Loading worked, so a later deploy gets its own single retry.
                forgetChunkReload();

                return page;
            })
            .catch(e => {
                if (e.name === 'ChunkLoadError' && rememberChunkReload()) {
                    window.location.reload();

                    // The page is on its way out; resolving now would render
                    // against a document that is about to be replaced.
                    return new Promise(() => {});
                }

                throw e;
            });
    },
    setup({ el, App, props, plugin }) {
        const app = createApp({ render: () => h(App, props) })
                    .use(plugin)
                    .use(ZiggyVue)
                    .component('PrimaryButton', PrimaryButton)
                    .provide('initAndGetAlgoliaEventDispatcher', initAndGetAlgoliaEventDispatcher)
                    .mount(el)

        app.$nextTick(() => {
            setTenantColors()
        });

        // Report DocsBot widget usage to FullStory. Installed once per full page load, not per
        // Inertia visit: the widget lives in the blade layout and survives client-side
        // navigation, so its listeners do too and re-attaching would double-count. No-ops on
        // a site with no bot, where no widget is mounted. See Helpers/DocsBotAnalytics.js.
        initDocsBotAnalytics()

        // Re-apply tenant colors on every Inertia client-side visit so each page
        // gets its own route's palette (e.g. internal_resources colors on /lesson-plans/*).
        router.on('navigate', () => {
            setTenantColors()
        });

    },
})
